import { Building2, Check, Leaf, Moon, Sun, Sunrise } from "lucide-react";
import { useAuth } from "wasp/client/auth";
import useColorMode from "../hooks/useColorMode";
import { THEMES, getTheme, type ThemeMeta } from "../theme/themes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { cn } from "../utils";
import useTranslation from "../hooks/useTranslation";

const ICONS: Record<ThemeMeta["icon"], React.ElementType> = {
  Moon, Sun, Leaf, Sunrise, Building2,
};

function applyThemeTransition(
  e: React.MouseEvent,
  apply: () => void,
) {
  // Apply first — never let the View Transition gate the actual change.
  apply();
  if (!("startViewTransition" in document)) return;
  try {
    const x = e.clientX;
    const y = e.clientY;
    const maxRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y),
    );
    const transition = (document as any).startViewTransition(() => {});
    transition.ready
      .then(() => {
        document.documentElement.animate(
          { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${maxRadius}px at ${x}px ${y}px)`] },
          { duration: 400, easing: "ease-in-out", pseudoElement: "::view-transition-new(root)" },
        );
      })
      .catch(() => {});
  } catch {
    /* unsupported / interrupted — theme already applied */
  }
}

const CLASSIC_THEME_IDS = ["dark", "light"];

const DarkModeSwitcher = ({
  restrictToClassic = false,
}: {
  /** When true, only Classic Dark / Classic Light are selectable (landing page). */
  restrictToClassic?: boolean;
}) => {
  const [colorMode, setColorMode] = useColorMode();
  const { data: user } = useAuth();
  const t = useTranslation();
  const isAdmin = !!(user as any)?.isAdmin;
  const ActiveIcon = ICONS[getTheme(colorMode).icon] ?? Moon;
  const themes = restrictToClassic
    ? THEMES.filter((theme) => CLASSIC_THEME_IDS.includes(theme.id))
    : THEMES;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t.themePicker.switchTo}
          className="relative flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ActiveIcon className="size-4" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {themes.map((theme) => {
          const Icon = ICONS[theme.icon];
          const isActive = colorMode === theme.id;
          const isLocked = !!theme.adminOnly && !isAdmin;
          const label = (t.themePicker as Record<string, string>)[theme.labelKey] ?? theme.id;
          return (
            <DropdownMenuItem
              key={theme.id}
              disabled={isLocked}
              className={cn(
                "flex items-center gap-2",
                isLocked ? "cursor-default opacity-50" : "cursor-pointer",
                isActive && "bg-accent",
              )}
              onClick={isLocked ? undefined : (e) =>
                applyThemeTransition(e, () => setColorMode(theme.id))
              }
            >
              <Icon className="size-3.5 shrink-0 opacity-70" />
              <span className="flex-1 text-sm">{label}</span>
              {isLocked ? (
                <span className="text-[10px] rounded px-1 py-0.5 bg-muted text-muted-foreground border border-border/50">
                  {t.themePicker.comingSoon}
                </span>
              ) : (
                isActive && <Check className="size-3 ml-1" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DarkModeSwitcher;
